import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "@/components/Logo";
import Powered360 from "@/components/Powered360";
import { toast } from "sonner";
import { Eye, EyeOff, Ticket } from "lucide-react";

export default function Signup() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [form, setForm] = useState({ name: "", restaurant_name: "", email: "", password: "", invite_code: "" });
    const [showPw, setShowPw] = useState(false);
    const [showCode, setShowCode] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const code = params.get("code");
        if (code) {
            setForm((f) => ({ ...f, invite_code: code.toUpperCase() }));
            setShowCode(true);
        }
    }, [params]);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (form.password.length < 6) {
            setError("Password must be at least 6 characters");
            return;
        }
        setLoading(true);
        setError("");
        const payload = { ...form };
        if (!payload.invite_code) delete payload.invite_code;
        const r = await register(payload);
        setLoading(false);
        if (r.ok) {
            toast.success(payload.invite_code ? "Invite redeemed! Welcome to FoodGlow360." : "Welcome to FoodGlow360! 10 free credits added.");
            navigate("/dashboard");
        } else {
            setError(r.error);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-zinc-950 grain px-6 py-12">
            <div className="absolute top-6 left-6">
                <Logo size="md" />
            </div>
            <div className="w-full max-w-md">
                <div className="glass-card rounded-3xl p-8 md:p-10">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-orange-400 mb-3 font-mono">Free trial · 10 credits</div>
                    <h1 className="font-heading text-3xl font-bold tracking-tight mb-7">Create your account</h1>
                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Your name</label>
                            <input
                                value={form.name}
                                onChange={set("name")}
                                required
                                data-testid="signup-name-input"
                                placeholder="Chef Marco Salazar"
                                className="mt-2 w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-orange-500/60 focus:border-orange-500/40"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Restaurant name (optional)</label>
                            <input
                                value={form.restaurant_name}
                                onChange={set("restaurant_name")}
                                data-testid="signup-restaurant-input"
                                placeholder="Tacos del Norte"
                                className="mt-2 w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-orange-500/60 focus:border-orange-500/40"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Email</label>
                            <input
                                type="email"
                                value={form.email}
                                onChange={set("email")}
                                required
                                data-testid="signup-email-input"
                                placeholder="you@restaurant.com"
                                className="mt-2 w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-orange-500/60 focus:border-orange-500/40"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Password</label>
                            <div className="mt-2 relative">
                                <input
                                    type={showPw ? "text" : "password"}
                                    value={form.password}
                                    onChange={set("password")}
                                    required
                                    minLength={6}
                                    data-testid="signup-password-input"
                                    placeholder="At least 6 characters"
                                    className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 pr-10 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-orange-500/60 focus:border-orange-500/40"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPw((v) => !v)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                                    aria-label="Toggle password visibility"
                                    data-testid="signup-toggle-password"
                                >
                                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                        {error && (
                            <div className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-4 py-2.5" data-testid="signup-error">
                                {error}
                            </div>
                        )}

                        {showCode ? (
                            <div>
                                <label className="text-xs font-mono uppercase tracking-widest text-orange-400 flex items-center gap-1.5">
                                    <Ticket className="w-3 h-3" /> Invite code
                                </label>
                                <input
                                    value={form.invite_code}
                                    onChange={(e) => setForm({ ...form, invite_code: e.target.value.toUpperCase() })}
                                    data-testid="signup-invite-code-input"
                                    placeholder="XXXX-XXXX-XXXX"
                                    className="mt-2 w-full bg-orange-500/8 border border-orange-500/30 rounded-xl px-4 py-3 text-orange-200 placeholder:text-orange-200/30 focus:outline-none focus:ring-2 focus:ring-orange-500/60 font-mono uppercase tracking-wider"
                                />
                                <button
                                    type="button"
                                    onClick={() => { setShowCode(false); setForm({ ...form, invite_code: "" }); }}
                                    className="mt-2 text-xs text-zinc-500 hover:text-zinc-300"
                                >
                                    Remove invite code
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setShowCode(true)}
                                data-testid="signup-show-code-btn"
                                className="text-xs text-zinc-500 hover:text-orange-300 inline-flex items-center gap-1.5"
                            >
                                <Ticket className="w-3 h-3" /> Have an invite code?
                            </button>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="signup-submit-btn"
                            className="btn-magic w-full py-3.5 rounded-xl text-white font-semibold mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? "Creating account..." : form.invite_code ? "Redeem & create account" : "Start free trial"}
                        </button>
                    </form>
                    <div className="mt-7 text-sm text-zinc-400 text-center">
                        Already a member?{" "}
                        <Link to="/login" data-testid="signup-login-link" className="text-orange-400 hover:text-orange-300 font-medium">
                            Sign in
                        </Link>
                    </div>
                </div>
                <p className="mt-5 text-xs text-zinc-600 text-center">
                    By creating an account you agree to the FoodGlow360 Terms & Privacy.
                </p>
                <div className="mt-5 flex justify-center">
                    <Powered360 variant="inline" />
                </div>
            </div>
        </div>
    );
}
